'use client';

import React from 'react';
import { useLanguage } from '@/context/LanguageContext';

export default function ServicesSection() {
  const { t } = useLanguage();

  const handleSelectSolution = (solutionName: string) => {
    const contactEl = document.getElementById('contacte');
    if (contactEl) {
      contactEl.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="solucions"
      className="py-24 px-6 bg-[#050507] relative overflow-hidden border-t border-white/5"
      aria-label="Solucions digitals Onix"
    >
      {/* Background glow effects */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[350px] bg-gradient-to-r from-purple-brand/20 via-magenta-brand/10 to-gold/15 blur-[160px] pointer-events-none" />

      <div className="max-w-7xl mx-auto space-y-16 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-gold/30 bg-gold/5 text-gold text-[11px] font-bold uppercase tracking-widest">
            {t.solutions.badge}
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight uppercase">
            {t.solutions.title}
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            {t.solutions.sub}
          </p>
        </div>

        {/* 2 Main Solution Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {/* Card 1: Onix Presència */}
          <div className="rounded-3xl p-8 sm:p-10 bg-gradient-to-b from-[#12131d] to-[#0a0a0f] border-2 border-gold/40 hover:border-gold shadow-2xl shadow-gold/5 transition-all duration-300 flex flex-col justify-between relative overflow-hidden">
            {/* Top Tag */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-[11px] font-black uppercase tracking-widest text-gold bg-gold/10 px-3 py-1 rounded-full border border-gold/30">
                  {t.solutions.p1Badge}
                </span>
                <span className="text-[11px] text-emerald-400 font-bold bg-emerald-500/10 px-2.5 py-0.5 rounded-full">
                  {t.solutions.p1SubBadge}
                </span>
              </div>

              <div>
                <h3 className="text-3xl font-black text-white">
                  {t.solutions.p1Name}
                </h3>
                <div className="flex items-baseline gap-2 pt-2">
                  <span className="text-4xl sm:text-5xl font-black text-gold">
                    {t.solutions.p1Price}
                  </span>
                </div>
                <p className="text-xs text-slate-400 pt-1">
                  {t.solutions.p1Period}
                </p>
              </div>

              <p className="text-sm text-slate-200 leading-relaxed pt-2">
                {t.solutions.p1Desc}
              </p>

              <div className="pt-4 border-t border-white/10 space-y-3">
                <p className="text-xs font-bold uppercase tracking-wider text-gold">
                  {t.solutions.whatsIncluded}
                </p>
                <ul className="space-y-2.5">
                  {t.solutions.p1Features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-3 text-xs sm:text-sm text-slate-300">
                      <span className="w-4 h-4 rounded-full bg-gold/20 text-gold flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5">
                        ✓
                      </span>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-4 rounded-xl bg-white/5 border border-white/5 text-xs text-slate-300">
                <strong className="text-white block pb-1">{t.solutions.recommendedFor}</strong>
                {t.solutions.p1Ideal}
              </div>
            </div>

            <div className="pt-8">
              <button
                onClick={() => handleSelectSolution('Onix Presència')}
                className="w-full py-4 rounded-xl text-xs sm:text-sm font-black uppercase tracking-wider text-black bg-gradient-to-r from-gold via-gold-light to-gold-dark hover:brightness-110 shadow-lg shadow-gold/20 transition-all duration-200 text-center"
              >
                {t.solutions.p1Cta}
              </button>
            </div>
          </div>

          {/* Card 2: Onix Sistema */}
          <div className="rounded-3xl p-8 sm:p-10 bg-gradient-to-b from-[#161224] to-[#0d0a14] border-2 border-purple-brand/50 hover:border-magenta-brand shadow-2xl shadow-purple-brand/10 transition-all duration-300 flex flex-col justify-between relative overflow-hidden">
            {/* Top Tag */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-[11px] font-black uppercase tracking-widest text-magenta-brand bg-magenta-brand/10 px-3 py-1 rounded-full border border-magenta-brand/30">
                  {t.solutions.p2Badge}
                </span>
                <span className="text-[11px] text-purple-200 font-bold bg-purple-brand/30 px-2.5 py-0.5 rounded-full border border-purple-brand/40">
                  {t.solutions.p2SubBadge}
                </span>
              </div>

              <div>
                <h3 className="text-3xl font-black text-white">
                  {t.solutions.p2Name}
                </h3>
                <div className="flex items-baseline gap-2 pt-2">
                  <span className="text-4xl sm:text-5xl font-black text-magenta-brand">
                    {t.solutions.p2Price}
                  </span>
                </div>
                <p className="text-xs text-slate-400 pt-1">
                  {t.solutions.p2Period}
                </p>
              </div>

              <p className="text-sm text-slate-200 leading-relaxed pt-2">
                {t.solutions.p2Desc}
              </p>

              <div className="pt-4 border-t border-white/10 space-y-3">
                <p className="text-xs font-bold uppercase tracking-wider text-magenta-brand">
                  {t.solutions.p2FeaturesTitle}
                </p>
                <ul className="space-y-2.5">
                  {t.solutions.p2Features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-3 text-xs sm:text-sm text-slate-300">
                      <span className="w-4 h-4 rounded-full bg-magenta-brand/20 text-magenta-brand flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5">
                        ✓
                      </span>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-4 rounded-xl bg-white/5 border border-white/5 text-xs text-slate-300">
                <strong className="text-white block pb-1">{t.solutions.recommendedFor}</strong>
                {t.solutions.p2Ideal}
              </div>
            </div>

            <div className="pt-8">
              <button
                onClick={() => handleSelectSolution('Onix Sistema')}
                className="w-full py-4 rounded-xl text-xs sm:text-sm font-black uppercase tracking-wider text-white bg-gradient-to-r from-purple-brand via-magenta-brand to-purple-brand hover:brightness-110 shadow-lg shadow-purple-brand/30 transition-all duration-200 text-center"
              >
                {t.solutions.p2Cta}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
